<!-- Analysis Summary tab – extracted from geo-technical-dashboard inline block -->
<div class="flex-1 min-h-0 overflow-hidden h-full">

    @if (analysisSummaryLoading && !analysisSummaryStatus) {
    <!-- Initial loading spinner — Lottie loading-spinner animation -->
    <div class="flex flex-col h-full w-full items-center justify-center gap-4 p-6">
        <app-lottie-animation [animationData]="queueAnimationData" [loop]="true" [autoplay]="true" width="80px"
            height="80px">
        </app-lottie-animation>
        <p class="text-sm text-gray-500 font-bricolage">Loading analysis summary…</p>
    </div>

    } @else if (analysisSummaryStatus === GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS) {
    <!-- Generation in progress: gradient title + Lottie + footer illustration -->
    <div class="flex flex-col h-full w-full justify-between">
        <div class="flex flex-col items-center gap-1 pt-8 px-6">
            <!-- Gradient title matching the IN_PROGRESS screenshot style -->
            <p class="font-bold text-xl md:text-2xl font-bricolage theme-text text-transparent text-center">
                Generating Analysis Summary
            </p>
            <p class="text-sm theme-text font-bricolage text-center">
                Please wait while we analyze your data and generate the summary.
            </p>
        </div>
        <!-- Generation Lottie animation -->
        <div class="relative flex items-center justify-center w-full h-56">
            <app-lottie-animation [animationData]="generationAnimationData" [loop]="true" [autoplay]="true"
                width="200px" height="200px" containerClass="geo-technical-summary-lottie">
            </app-lottie-animation>
        </div>
        <div class="flex items-end justify-center overflow-hidden">
            <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE | sanitizeHtml"
                class="[&>svg]:w-[500px] [&>svg]:h-[200px] pl-5" alt="Summarize"></i>
        </div>
    </div>

    } @else if (analysisSummaryStatus === GEOTECHNICAL_SUMMARY_STATUS.FAILED) {
    <!-- Generation failed state: dark title + neutral subtitle + orange Retry button -->
    <div class="flex flex-col h-full w-full justify-between">
        <div class="flex flex-col items-center gap-1 pt-8 px-6">
            <p class="font-bold text-xl md:text-2xl font-bricolage text-gray-800 text-center">
                Failed to generate Analysis Summary
            </p>
            <p class="text-sm text-gray-500 font-bricolage text-center">
                We couldn't prepare your analysis summary right now. Please retry.
            </p>
        </div>
        <!-- Orange Retry button with rotate-ccw icon -->
        <div class="flex items-center justify-center">
            <app-button label="Retry" icon="rotate-ccw" iconSource="lucide" severity="primary" variant="filled"
                (clicked)="onSubmitAnalysisSummaryDescription()">
            </app-button>
        </div>
        <div class="flex items-end justify-center overflow-hidden">
            <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE | sanitizeHtml"
                class="[&>svg]:w-[500px] [&>svg]:h-[200px] pl-5" alt="Summarize"></i>
        </div>
    </div>

    } @else if (analysisSummaryStatus === GEOTECHNICAL_SUMMARY_STATUS.NOT_STARTED) {
    <!-- Not started / in-queue state: gradient title + Lottie spinner + footer illustration -->
    <div class="flex flex-col h-full w-full justify-between">
        <div class="flex flex-col items-center gap-1 pt-8 px-6">
            <p class="font-bold text-xl md:text-2xl font-bricolage theme-text text-transparent text-center">
                Analysis Summary is in queue
            </p>
            <p class="text-sm theme-text font-bricolage text-center">
                Your analysis summary request is waiting its turn and will begin soon.
            </p>
        </div>
        <!-- Loading spinner Lottie for the queued state -->
        <div class="flex items-center justify-center">
            <app-lottie-animation [animationData]="queueAnimationData" [loop]="true" [autoplay]="true" width="80px"
                height="80px">
            </app-lottie-animation>
        </div>
        <div class="flex items-end justify-center overflow-hidden">
            <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE | sanitizeHtml"
                class="[&>svg]:w-[500px] [&>svg]:h-[200px] pl-5" alt="Summarize"></i>
        </div>
    </div>

    } @else if (openAnalysisSummaryHistory) {
    <!-- History sub-view -->
    <div class="flex flex-col h-full p-3 w-full">
        <div class="flex items-center gap-2 border-gray-200">
            <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text"
                (clicked)="openAnalysisSummaryHistory = false">
            </app-button>
            <p class="font-semibold text-lg">Analysis Summary</p>
        </div>

        @if (loadingAnalysisSummaryHistory) {
        <div class="flex justify-center items-center flex-1 p-6">
            <div class="loader"></div>
        </div>
        } @else if (analysisSummaryHistoryList.length === 0) {
        <div class="flex flex-col items-center justify-center flex-1 p-6 text-gray-500">
            <span>No analysis summary history yet.</span>
        </div>
        } @else {
        <div class="flex flex-col p-4 overflow-auto">
            @for (item of analysisSummaryHistoryList; track item._id ?? $index) {
            <div class="flex flex-row items-start justify-between gap-3 py-2 border-b border-gray-200 last:border-b-0">
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                    <span class="text-sm text-gray-500">
                        {{ item.createdAt ? (item.createdAt | date:'d MMMM yyyy') : 'Analysis Summary' }}
                    </span>
                    <div class="flex items-center">
                        <app-user-name-display [user]="item.createdBy" [variant]="'avatar-name'"></app-user-name-display>
                    </div>
                </div>
                <app-popover #analysisSummaryItemPopover [width]="'10rem'" class="flex-shrink-0">
                    <ng-template #trigger>
                        <div class="p-1.5 rounded-base hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 flex items-center justify-center">
                            <i class="pi pi-ellipsis-v text-base"></i>
                        </div>
                    </ng-template>
                    <ng-template #body>
                        <div class="flex flex-col gap-0.5 py-1" (click)="$event.stopPropagation()">
                            @if (!isViewer) {
                            <button type="button"
                                class="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-base transition-colors text-left"
                                (click)="onDownloadAnalysisSummaryItem(item._id); analysisSummaryItemPopover.hide()">
                                <i class="pi pi-download text-gray-600"></i>
                                <span>Download</span>
                            </button>
                            }
                            <button type="button"
                                class="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-base transition-colors text-left"
                                (click)="onDeleteAnalysisSummaryItem(item); analysisSummaryItemPopover.hide()">
                                <i class="pi pi-trash"></i>
                                <span>Delete</span>
                            </button>
                        </div>
                    </ng-template>
                </app-popover>
            </div>
            }
        </div>
        }
    </div>

    } @else {
    <!-- Completed / default: show the summary content -->
    <div class="flex flex-col h-full w-full min-h-0">
        <div class="flex flex-shrink-0 justify-between items-center px-6 pb-4 pt-4 w-full">
            <p class="flex items-center font-semibold text-lg gap-2">
                <span>Analysis Summary</span>
                <app-icon [type]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [source]="'svg'" [size]="'default'" />
            </p>
            <div class="flex gap-3">
                @if (!isViewer) {
                <app-button
                    [icon]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED"
                    [iconSource]="'svg'"
                    [size]="'default'"
                    (clicked)="analysisSummaryDescriptionDialogVisible = true"
                    variant="outlined"
                    severity="contrast"
                    pTooltip="Re-Generate"
                    tooltipPosition="top">
                </app-button>
                <app-button
                    [icon]="SVG.DOWNLOAD"
                    [iconSource]="'svg'"
                    [size]="'default'"
                    variant="outlined"
                    pTooltip="Download"
                    tooltipPosition="top"
                    (clicked)="onDownloadAnalysisSummaryItem(latestSummaryDetails._id)"
                    [disabled]="downloadLoading()">
                </app-button>
                }
                <app-button
                    [icon]="SVG.VERSION"
                    [iconSource]="'svg'"
                    [size]="'default'"
                    variant="outlined"
                    pTooltip="History"
                    tooltipPosition="top"
                    (clicked)="onAnalysisSummaryHistoryClick()">
                </app-button>
            </div>
        </div>
        <div class="flex-1 min-h-0 max-h-[calc(100vh-14rem)] overflow-y-auto overflow-x-hidden scrollbar-thin">
            <div class="prose prose-compact text-justify text-sm px-6 pb-6 highlight-transition rounded-base prose-ul:pl-1 prose-ol:pl-2 2xl:prose-ul:pl-4 2xl:prose-ol:pl-3 sm:prose-ul:pl-3"
                [innerHTML]="latestSummaryDetails.summary | formatText"
                style="line-height: 1.5rem;">
            </div>
        </div>
    </div>
    }
</div>

<!-- Re-generate description dialog -->
<app-dialog
    [visible]="analysisSummaryDescriptionDialogVisible"
    (closed)="analysisSummaryDescriptionDialogVisible = false"
    title="Analysis Summary Description"
    class="max-w-[40rem]">
    <ng-template #bodyTemplate>
        <div class="flex flex-col">
            <textarea
                placeholder="Type here"
                rows="5"
                class="w-full p-3 border border-gray-300 rounded-base text-sm outline-none placeholder:italic placeholder:text-gray-400 focus:border-orange-500 resize-none"
                [(ngModel)]="analysisSummaryDescriptionInput">
            </textarea>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button
                label="Generate"
                severity="primary"
                variant="filled"
                (clicked)="onSubmitAnalysisSummaryDescription()" />
        </div>
    </ng-template>
</app-dialog>
